import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import DashboardIcon from '@/assets/icons/DashboardIcon';
import { Flex, VFlex } from '@/components/base';
import List from '@/components/List/List';
import Modal, { useModalContext } from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import type { Dashboard2Base } from '@/features/Dashboard/types';
import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { getTimezoneDayjs } from '@/utils/time';

type Props = {
  onConfirm: (dashboard: Dashboard2Base) => void;
};

const SelectDashboardModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard2-list'],
    queryFn: () => {
      return service.dashboard2.fetchDashboardList({
        sort_by: 'updated_at',
        order_by: 'desc',
      });
    },
  });

  const [selectedDashboard, setSelectedDashboard] =
    useState<Dashboard2Base | null>(null);

  return (
    <Modal.Layout modalSize="m">
      <Modal.Header>
        <Modal.Title>Select Dashboard</Modal.Title>
        <Modal.Desc>Select a dashboard to create a report.</Modal.Desc>
      </Modal.Header>
      <Modal.Body>
        <List
          className="h-75 border border-gray-400 rounded-md p-2"
          data={data?.dashboards}
          render={(item) => (
            <DashboardItem
              key={item.alias}
              dashboard={item}
              selected={selectedDashboard?.alias === item.alias}
              onClick={() => setSelectedDashboard(item)}
            />
          )}
          isLoading={isLoading}
        />
      </Modal.Body>
      <Modal.Footer
        cancel={{
          onClick: closeModal,
        }}
        confirm={{
          onClick: () => {
            if (selectedDashboard) {
              props.onConfirm(selectedDashboard);
              closeModal();
            }
          },
        }}
      />
    </Modal.Layout>
  );
};

type DashboardItemProps = {
  dashboard: Dashboard2Base;
  selected: boolean;
  onClick: () => void;
};

const DashboardItem = (props: DashboardItemProps) => {
  const members = useWorkspaceStore((state) => state.currWorkspaceMembers);
  const member = members.find((m) => m.user_id === props.dashboard.updated_by);
  return (
    <DashboardItemStyle
      g={12}
      a="center"
      selected={props.selected}
      onClick={props.onClick}
    >
      <DashboardIcon style={{ fontSize: 16 }} />
      <VFlex g={4}>
        <Typo type="label2" weight="sb" className="text-gray-800">
          {props.dashboard.title}
        </Typo>
        <Typo type="label3" weight="r" className="text-gray-700">
          {getTimezoneDayjs(props.dashboard.updated_at).fromNow()} •{' '}
          {member?.first_name}
        </Typo>
      </VFlex>
    </DashboardItemStyle>
  );
};

type ItemStyleProps = {
  selected: boolean;
};

const DashboardItemStyle = styled(Flex)<ItemStyleProps>`
  padding: 12px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.gray100};
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray200};
  }
  ${({ selected }) =>
    selected &&
    css`
      background-color: ${({ theme }) => theme.colors.gray250};
    `}
`;

export default SelectDashboardModal;
